// Compact Fast Fit strip on a subject guide: return, family context, prev/next inside the family, same 1–3 plan.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { usePublishedGuides } from "@/lib/subjectGuides";
import { familySlug, guideHref, resultsHref } from "@/lib/fastFitGuideContext";
import { fetchFamilyPreviewFlag } from "@/pages/beta/courseFitApi";

const chip = "inline-flex min-h-[32px] items-center gap-1 rounded-full border px-3 text-xs font-medium !no-underline transition-colors";

function familyGuides(fam, bySubjectId) {
  return (fam?.members || []).filter((m) => bySubjectId.has(m.subjectId)).map((m) => ({ ...m, guide: bySubjectId.get(m.subjectId) }));
}

function FamilyNav({ fam, guides, idx, name }) {
  const prev = idx > 0 ? guides[idx - 1] : null;
  const next = idx >= 0 && idx < guides.length - 1 ? guides[idx + 1] : null;
  if (idx < 0) {
    const first = guides[0];
    return (
      <p className="text-xs text-slate-600" data-testid="ff-strip-outside">
        {name} isn't in {fam.family}.{" "}
        {first && <Link to={guideHref(first.guide.slug, fam.slug)} className="font-medium !text-[#7C3AED] underline" data-testid="ff-strip-back-to-family">Back to {fam.family}</Link>}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="ff-strip-family-nav">
      {prev ? <Link to={guideHref(prev.guide.slug, fam.slug)} className={`${chip} border-slate-300 !text-slate-800 hover:border-slate-900`} data-testid="ff-strip-prev"><ChevronLeft className="h-3 w-3" aria-hidden="true" />{prev.canonicalName}</Link>
        : <span className="text-slate-400" data-testid="ff-strip-prev-none">First guide in this family</span>}
      <span className="text-slate-500" data-testid="ff-strip-position">{idx + 1} of {guides.length} published guides</span>
      {next ? <Link to={guideHref(next.guide.slug, fam.slug)} className={`${chip} border-slate-300 !text-slate-800 hover:border-slate-900`} data-testid="ff-strip-next">{next.canonicalName}<ChevronRight className="h-3 w-3" aria-hidden="true" /></Link>
        : <span className="text-slate-400" data-testid="ff-strip-next-none">Last guide in this family</span>}
    </div>
  );
}

function FamilySwitch({ chosen, explored, active, bySubjectId }) {
  const fams = chosen.map((slug) => explored.find((f) => f.slug === slug)).filter(Boolean);
  const outside = !chosen.includes(active);
  if (fams.length < 2 && !outside) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="ff-strip-family-switch">
      {fams.map((f) => {
        const first = familyGuides(f, bySubjectId)[0];
        const cls = `${chip} ${f.slug === active ? "border-[#7C3AED] bg-[#7C3AED] !text-white" : "border-[#7C3AED] !text-[#7C3AED] hover:bg-[#F5F0FF]"}`;
        return first
          ? <Link key={f.slug} to={guideHref(first.guide.slug, f.slug)} className={cls} aria-current={f.slug === active ? "true" : undefined} data-testid={`ff-strip-family-${f.slug}`}>{outside ? `Back to ${f.family}` : f.family}</Link>
          : <Link key={f.slug} to={resultsHref(f.slug)} className={cls} data-testid={`ff-strip-family-${f.slug}`}>{f.family} (no guides yet)</Link>;
      })}
    </div>
  );
}

function PlanControl({ ff, name, member, canAdd }) {
  const inPlan = ff.names.includes(name);
  const full = !inPlan && ff.names.length >= ff.cap;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="ff-strip-plan">
      {member && !member.scored && <span className="text-slate-500" data-testid="ff-strip-unscored">Exploratory · not scored yet (that says nothing about your fit)</span>}
      {member?.scored && <span className="text-slate-700" data-testid="ff-strip-score">Fast Match score {member.displayScore}</span>}
      {canAdd || inPlan ? (
        <button type="button" disabled={ff.busy || full} onClick={() => ff.change(name, !inPlan)} data-testid="ff-strip-plan-toggle"
          className={`${chip} !rounded-full !px-3 !py-0 ${inPlan ? "!border-[#7C3AED] !bg-[#7C3AED] !text-white" : full ? "cursor-not-allowed !border-stone-200 !text-stone-400" : "!border-[#7C3AED] !bg-white !text-[#7C3AED] hover:!bg-[#F5F0FF]"}`}>
          {inPlan ? <><Check className="h-3 w-3" aria-hidden="true" /> In my plan · Remove</> : full ? "Plan full (3 of 3)" : "+ Add to my plan"}
        </button>
      ) : <span className="text-slate-500" data-testid="ff-strip-not-addable">Explore this subject's family on your results page to add it.</span>}
      <span className="text-slate-600" data-testid="ff-strip-plan-summary">My plan ({ff.names.length}/{ff.cap}): {ff.names.join(", ") || "none yet"}</span>
      {ff.msg && <span role="status" className="text-amber-800" data-testid="ff-strip-msg">{ff.msg}</span>}
    </div>
  );
}

export function FastFitStrip({ ff, subjectId, name }) {
  const { bySubjectId, loaded } = usePublishedGuides();
  if (ff.status === "stale") {
    return (
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 print:hidden" data-testid="ff-strip-stale">
        Your Fast Fit result has changed since you opened this guide. <Link to={resultsHref()} className="font-medium underline" data-testid="ff-strip-return">Back to my results</Link>
      </div>
    );
  }
  if (ff.status !== "ready" || !loaded || !ff.params) return null;
  const { view, ctx } = ff;
  const explored = [...(view.explored || [])];
  // A family not explored yet (e.g. opened from a top-5 card): its published guides from the catalogue.
  const catFam = (slug) => {
    const gs = [...bySubjectId.values()].filter((g) => familySlug(g.family) === slug).sort((a, b) => a.name.localeCompare(b.name));
    return gs.length ? { slug, family: gs[0].family, members: gs.map((g) => ({ subjectId: g.subject_id, canonicalName: g.name })) } : null;
  };
  const chosen = view.choice?.mode === "families" ? view.choice.families : (ctx.selected || []);
  [ff.params.family, ...chosen].forEach((slug) => {
    if (slug && !explored.some((f) => f.slug === slug)) { const f = catFam(slug); if (f) explored.push(f); }
  });
  const active = explored.some((f) => f.slug === ff.params.family) ? ff.params.family : chosen.find((c) => explored.some((f) => f.slug === c)) || explored[0]?.slug;
  const fam = explored.find((f) => f.slug === active);
  const guides = familyGuides(fam, bySubjectId);
  const idx = guides.findIndex((m) => m.subjectId === subjectId);
  const member = (view.explored || []).flatMap((f) => f.members).find((m) => m.subjectId === subjectId);
  const canAdd = (view.historySubjects || []).some((m) => m.canonicalName === name) || (ctx.pickable || []).includes(name);
  const label = member?.canonicalName || name;
  return (
    <aside className="mb-4 rounded-xl border border-[#7C3AED]/40 bg-white/95 p-3 shadow-sm backdrop-blur print:hidden" aria-label="Your Fast Fit exploration" data-testid="ff-strip">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={resultsHref(active)} className="inline-flex items-center gap-1 text-sm font-medium !text-[#7C3AED] !no-underline hover:!underline" data-testid="ff-strip-return">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to my families &amp; results
        </Link>
        {fam && <span className="text-xs text-slate-600" data-testid="ff-strip-family">Exploring: <b className="font-semibold text-slate-900">{fam.family}</b></span>}
      </div>
      <div className="mt-2 space-y-2">
        <FamilySwitch chosen={chosen} explored={explored} active={active} bySubjectId={bySubjectId} />
        {fam && <FamilyNav fam={fam} guides={guides} idx={idx} name={label} />}
        <PlanControl ff={ff} name={label} member={member} canAdd={canAdd} />
      </div>
    </aside>
  );
}

// Direct entrants (search/browse): unobtrusive way into Fast Fit; never required to read a guide.
export function ExploreFitLink() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let live = true;
    fetchFamilyPreviewFlag().then((r) => live && setOn(!!(r.ok && r.data?.enabled)));
    return () => { live = false; };
  }, []);
  if (!on) return null;
  return (
    <p className="mb-3 text-right text-xs print:hidden">
      <Link to="/beta/course-fit" className="font-medium !text-[#7C3AED] !no-underline hover:!underline" data-testid="ff-explore-fit-link">Explore your subject fit →</Link>
    </p>
  );
}
